import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { path: '/', label: '首页', icon: 'ri-home-line' },
  { path: '/category/movie', label: '电影', icon: 'ri-movie-line' },
  { path: '/category/tv', label: '电视剧', icon: 'ri-tv-line' },
  { path: '/category/variety', label: '综艺', icon: 'ri-music-line' },
  { path: '/category/anime', label: '动漫', icon: 'ri-rocket-line' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-xl shadow-[0_1px_0_0_rgba(255,255,255,0.05)]'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6" aria-label="主导航">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 shrink-0 group"
            aria-label="春夜影院首页"
          >
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-primary-500 rounded-lg transition-transform duration-300 group-hover:scale-105">
              <i className="ri-movie-2-line text-lg md:text-xl text-background-50"></i>
            </div>
            <span className="text-lg md:text-xl font-bold font-heading text-foreground-50 tracking-wide whitespace-nowrap">
              春夜影院
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                  isActive(link.path)
                    ? 'text-foreground-50 bg-primary-500/20'
                    : 'text-foreground-300 hover:text-foreground-50 hover:bg-foreground-50/5'
                }`}
              >
                <i className={`${link.icon} mr-1.5 text-sm`}></i>
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Search Toggle */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-300 hover:text-foreground-50 hover:bg-foreground-50/5 transition-all duration-300 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full text-foreground-300 hover:text-foreground-50 hover:bg-foreground-50/5 transition-all duration-300 cursor-pointer"
              aria-label={mobileOpen ? '关闭菜单' : '打开菜单'}
            >
              <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div
          className={`overflow-hidden transition-all duration-400 ${
            searchOpen ? 'max-h-16 pb-3' : 'max-h-0'
          }`}
        >
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="搜索电影、电视剧、综艺、动漫..."
              className="w-full pl-10 pr-4 py-2.5 text-sm rounded-full bg-background-100 border border-background-200 text-foreground-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-all duration-300"
            />
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-400 ${
          mobileOpen ? 'max-h-80' : 'max-h-0'
        }`}
      >
        <div className="px-4 pb-4 space-y-1 bg-background-50/98 backdrop-blur-xl">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                isActive(link.path)
                  ? 'text-foreground-50 bg-primary-500/15'
                  : 'text-foreground-300 hover:text-foreground-50 hover:bg-foreground-50/5'
              }`}
            >
              <i className={`${link.icon} text-base`}></i>
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
}